import { memo, useId, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Badge, Checkbox, getUserFullName } from '@bliks-io/ui';
import type {
  DataSyncDifference,
  DataSourceKind,
  DataSyncLeaverHolds,
  DataSyncRef,
  DataSyncUnitUsage
} from '@bliks-io/contracts';

const NO_VALUE = '—';

type PersonUpdate = Extract<DataSyncDifference, { kind: 'personUpdate' }>;

function joined(parts: (string | null | undefined)[]) {
  return parts.filter(Boolean).join(' · ');
}

function changed(before: string | null, after: string) {
  return `${before ?? NO_VALUE} → ${after}`;
}

function fieldValue(value: NonNullable<PersonUpdate['before']> | PersonUpdate['after']) {
  if ('firstName' in value) return getUserFullName(value.firstName, value.lastName);
  if ('email' in value) return value.email;
  if ('location' in value) return value.location;
  return value.name;
}

function leaverReasonKey(reason: 'inactive' | 'absent', sourceKind: DataSourceKind) {
  if (reason === 'inactive') return 'dataSync.sync.leaverReason.inactive';
  return sourceKind === 'personio'
    ? 'dataSync.sync.leaverReason.absentPersonio'
    : 'dataSync.sync.leaverReason.absentExcel';
}

/** The row's name, and the line under it naming what changes. */
export function describeDifference(
  difference: DataSyncDifference,
  sourceKind: DataSourceKind,
  t: TFunction
): { title: string; meta: string } {
  switch (difference.kind) {
    case 'departmentCreate':
      return {
        title: difference.name,
        meta: joined([
          t('dataSync.sync.kind.departmentCreate'),
          difference.parentName && t('dataSync.sync.under', { name: difference.parentName })
        ])
      };
    case 'unitRename':
      return {
        title: changed(difference.before, difference.unit.name),
        meta: t(`dataSync.sync.kind.unitRename.${difference.unit.type}`)
      };
    case 'departmentMove': {
      const topLevel = t('dataSync.sync.topLevel');
      return {
        title: difference.departmentName,
        meta: joined([
          t('dataSync.sync.kind.departmentMove'),
          changed(difference.beforeParentName ?? topLevel, difference.afterParentName ?? topLevel)
        ])
      };
    }
    case 'teamCreate':
      return { title: difference.name, meta: t('dataSync.sync.kind.teamCreate') };
    case 'roleCreate':
      return { title: difference.name, meta: t('dataSync.sync.kind.roleCreate') };
    case 'systemCreate':
      return {
        title: difference.name,
        meta: joined([
          t('dataSync.sync.kind.systemCreate'),
          difference.owner &&
            t('dataSync.sync.labelled', {
              label: t('dataSync.sync.kind.unitLead.system'),
              value: difference.owner.displayName
            })
        ])
      };
    case 'personCreate':
      return {
        title: getUserFullName(difference.firstName, difference.lastName),
        meta: joined([
          difference.email,
          difference.departmentName,
          difference.role,
          difference.teamName,
          difference.location
        ])
      };
    case 'personUpdate':
      return {
        title: difference.displayName,
        meta: t('dataSync.sync.labelled', {
          label: t(`dataSync.sync.field.${difference.field}`),
          value: changed(
            difference.before && fieldValue(difference.before),
            fieldValue(difference.after)
          )
        })
      };
    case 'unitLead':
      return {
        title: difference.unit.name,
        meta: t('dataSync.sync.labelled', {
          label: t(`dataSync.sync.kind.unitLead.${difference.unit.type}`),
          value: changed(difference.before?.displayName ?? null, difference.after.displayName)
        })
      };
    case 'leaver':
      return {
        title: difference.displayName,
        meta: joined([
          difference.departmentName,
          difference.role,
          t(leaverReasonKey(difference.reason, sourceKind))
        ])
      };
    case 'unitDelete':
      return {
        title: difference.unit.name,
        meta: t(`dataSync.sync.kind.unitDelete.${difference.unit.type}`)
      };
  }
}

interface UseBadge {
  key: string;
  label: string;
}

/** What a leaver still holds, one badge each; an empty hold has none. */
function holdBadges(holds: DataSyncLeaverHolds, t: TFunction): UseBadge[] {
  const leads = (refs: DataSyncRef[]) =>
    refs.map(({ id, name }) => ({ key: id, label: t('dataSync.sync.holds.leads', { name }) }));
  const counted = (key: string, count: number) =>
    count > 0 ? [{ key, label: t(`dataSync.sync.holds.${key}`, { count }) }] : [];
  return [
    ...leads(holds.departmentsManaged),
    ...leads(holds.functionsLed),
    ...counted('processesOwned', holds.processesOwned.length),
    ...counted('processesAccountable', holds.processesAccountable.length),
    ...holds.systemsOwned.map(({ id, name }) => ({
      key: id,
      label: t('dataSync.sync.holds.systemOwned', { name })
    })),
    ...counted('openAssignments', holds.openAssignmentCount)
  ];
}

/** What still uses a unit to delete, one badge per kind of use. */
function usageBadges(usage: DataSyncUnitUsage, t: TFunction): UseBadge[] {
  return (Object.keys(usage) as (keyof DataSyncUnitUsage)[])
    .filter((key) => usage[key] > 0)
    .map((key) => ({ key, label: t(`dataSync.sync.usage.${key}`, { count: usage[key] }) }));
}

function badgesOf(difference: DataSyncDifference, t: TFunction): UseBadge[] {
  if (difference.kind === 'leaver') return holdBadges(difference.holds, t);
  if (difference.kind === 'unitDelete') return usageBadges(difference.usage, t);
  return [];
}

export interface DifferenceText {
  title: string;
  meta: string;
  blockedReason?: string;
  badges: UseBadge[];
}

/** Everything a difference row shows, translated once per step. */
export function differenceText(
  difference: DataSyncDifference,
  sourceKind: DataSourceKind,
  t: TFunction
): DifferenceText {
  return {
    ...describeDifference(difference, sourceKind, t),
    blockedReason: difference.blockedReason
      ? t(`dataSync.sync.blockedReason.${difference.blockedReason}`)
      : undefined,
    badges: badgesOf(difference, t)
  };
}

interface SyncRowProps {
  title: string;
  meta: string;
  blockedReason?: string;
  /** Why an unblocked row cannot be selected yet. */
  waitingOn?: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
  children?: ReactNode;
}

const SyncRow = memo(function SyncRow({
  title,
  meta,
  blockedReason,
  waitingOn,
  checked,
  disabled,
  onCheckedChange,
  children
}: SyncRowProps) {
  // Generated, not difference ids: an Excel department key is a name and may contain spaces.
  const id = useId();
  const describedBy =
    [blockedReason && `${id}-blocked`, waitingOn && `${id}-waiting`].filter(Boolean).join(' ') ||
    undefined;
  return (
    <li className="flex items-start gap-3 py-2.5">
      <Checkbox
        id={id}
        className="mt-0.5"
        aria-labelledby={`${id}-title ${id}-meta`}
        aria-describedby={describedBy}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(state) => onCheckedChange(state === true)}
      />
      <div className="min-w-0 flex-1">
        <label htmlFor={id} className="block">
          <span id={`${id}-title`} className="block text-sm font-medium text-foreground">
            {title}
          </span>
          <span id={`${id}-meta`} className="block text-xs text-muted-foreground">
            {meta}
          </span>
        </label>
        {blockedReason && (
          <div id={`${id}-blocked`} className="text-xs text-warning">
            {blockedReason}
          </div>
        )}
        {waitingOn && (
          <div id={`${id}-waiting`} className="text-xs text-muted-foreground">
            {waitingOn}
          </div>
        )}
        {children}
      </div>
    </li>
  );
});

interface SyncDifferenceRowProps {
  difference: DataSyncDifference;
  text: DifferenceText;
  waitingOn?: string;
  checked: boolean;
  disabled: boolean;
  onToggle: (targets: DataSyncDifference[], checked: boolean) => void;
}

export const SyncDifferenceRow = memo(function SyncDifferenceRow({
  difference,
  text: { title, meta, blockedReason, badges },
  waitingOn,
  checked,
  disabled,
  onToggle
}: SyncDifferenceRowProps) {
  return (
    <SyncRow
      title={title}
      meta={meta}
      blockedReason={blockedReason}
      waitingOn={waitingOn}
      checked={checked}
      disabled={disabled}
      onCheckedChange={(next) => onToggle([difference], next)}
    >
      {badges.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {badges.map(({ key, label }) => (
            <Badge key={key} colorScheme="warning">
              {label}
            </Badge>
          ))}
        </div>
      )}
    </SyncRow>
  );
});
